import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode, type RefObject } from 'react'
import { BoardView } from '@/components/conversations/BoardView'
import { isWorkflowInProgress as checkWorkflowInProgress } from '@/lib/workflow-utils'
import { useTranslation, Trans } from 'react-i18next'
import { getDateLocale } from '@/lib/i18n'
import type { ActionLogEntry } from '@/hooks/useBrowserUse' // Used in props interface
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkSectionize from 'remark-sectionize'
import { CollapsibleSection } from '@/components/ui/collapsible-section'
import { csrfHeaders } from '@/lib/csrf'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverClose,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { RefreshCw, Monitor, Smartphone, FileText, ExternalLink, KeyRound, Copy, Check, Globe, Eye, EyeOff, ChevronDown, ChevronUp, SquareKanban, CheckCircle2, Circle, Clock, Loader2, AlertCircle, Pause, PlayCircle, X, ArrowRight, MonitorSmartphone, Code2, Database, DraftingCompass, Bot, TerminalSquare, Wrench, BookOpen, ListChecks, Settings2 } from 'lucide-react'
import { SandboxSettingsPopover } from './SandboxSettingsPopover'
import { QRCodeSVG } from 'qrcode.react'

const WARMING_UP_TIMEOUT = 120 * 1000

const PHONE_WIDTH = 340
const PHONE_HEIGHT = 711

// Scales the phone to fit the container while keeping it centered
function ScaledPhoneContainer({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      const padding = 32
      const scaleX = (width - padding) / PHONE_WIDTH
      const scaleY = (height - padding) / PHONE_HEIGHT
      setScale(Math.min(1, scaleX, scaleY))
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} className="h-full w-full flex items-center justify-center">
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
        {children}
      </div>
    </div>
  )
}

// Helper to derive mobile app URL from web URL (for iframe preview)
function getMobileAppUrl(webUrl: string | undefined): string | undefined {
  if (!webUrl) return undefined
  try {
    const url = new URL(webUrl)
    // Local development: change port from 5173 to 8081
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      url.port = '8081'
      return url.toString()
    }
    // Production: prepend 'mobile-' to subdomain
    // e.g., myproject.sandbox.cayu.app -> mobile-myproject.sandbox.cayu.app
    const hostParts = url.hostname.split('.')
    if (hostParts.length >= 3) {
      hostParts[0] = `mobile-${hostParts[0]}`
      url.hostname = hostParts.join('.')
      return url.toString()
    }
    return undefined
  } catch {
    return undefined
  }
}

import type { Project, GroupedTodos, HistoryEntry, Message, Pagination, ActiveWorkflow } from '@/types/conversations'
import { HistoryTab } from './HistoryTab'
import { IntegrationsTab } from './IntegrationsTab'
import { LiveIntegrationsSection } from './LiveIntegrationsSection'
import { DjangoLiveSection } from './DjangoLiveSection'
import { DnsManagementSection } from './DnsManagementSection'
import { VanitySubdomainSection } from './VanitySubdomainSection'
import { HintsDisplay } from './HintsDisplay'
import { AssetsView } from '@/components/assets/AssetsView'
import { SettingsTab } from './SettingsTab'
import { CodeTab } from '@/components/code/CodeTab'
import { DatabaseTab } from '@/components/database/DatabaseTab'
import { IPhoneFrame } from './IPhoneFrame'
import { IPhoneStatusBar } from './IPhoneStatusBar'
import { MobilePreviewSidePanel } from './MobilePreviewSidePanel'

interface SelectedElementContext {
  selector: string
  cayuSelector?: string
  tag: string
  text: string
  route: string
  appType?: 'web' | 'mobile'
}

interface PreviewPaneProps {
  project: Project
  isProvisioning?: boolean
  activeTab: string
  isChatHidden?: boolean
  onToggleChat?: () => void
  messages?: Message[]
  historyEntries?: HistoryEntry[]
  historyPagy?: Pagination
  processing?: boolean
  groupedTodos?: GroupedTodos
  /** When true, only renders the active tab content. When false (default), renders all tabs with CSS show/hide for scroll preservation */
  renderOnlyActiveTab?: boolean
  /** Track which tabs have been visited - mount on first access, stay mounted */
  visitedTabs?: Set<string>
  /** Current conversation mode */
  conversationMode?: 'planning' | 'implementation' | 'prototype' | 'discussion'
  /** Element selection mode */
  isSelectionModeActive?: boolean
  onElementSelected?: (element: SelectedElementContext) => void
  selectedElement?: SelectedElementContext | null
  /** Iframe ref for browser use testing */
  iframeRef?: RefObject<HTMLIFrameElement | null>
  /** Mobile iframe ref for browser use testing (Expo Web) */
  mobileIframeRef?: RefObject<HTMLIFrameElement | null>
  /** Browser use testing state */
  isBrowserUseTesting?: boolean
  browserUseCurrentAction?: string | null
  browserUseActionLog?: ActionLogEntry[]
  onStopBrowserUseTesting?: () => void
  /** Controlled device view (for browser-use preview control) */
  deviceView?: 'desktop' | 'mobile'
  onDeviceViewChange?: (view: 'desktop' | 'mobile') => void
  /** Controlled app type (web or mobile) */
  appType?: 'web' | 'mobile'
  onAppTypeChange?: (type: 'web' | 'mobile') => void
  /** Controlled iframe key for forced reloads (for browser-use preview control) */
  iframeKey?: number
  /** Callback for refresh (when iframeKey is controlled) */
  onRefresh?: () => void
  /** Organization for assets tab */
  organization?: {
    id: number
    name: string
    credits_remaining: number
    current_plan: string
  }
  /** Deployment tracking - view deployed entry in history */
  onViewDeployedInHistory?: () => void
  /** Rewinding state - for restricting actions during rewind */
  isRewinding?: boolean
  onRewindingChange?: (isRewinding: boolean) => void
  /** Live-updated credentials from ActionCable */
  liveCredentials?: {
    rails_app_password?: string
    rails_app_admin_email?: string
  } | null
  /** Live-updated product spec from ActionCable */
  liveProductSpec?: {
    content: string
    updated_at: string
  } | null
  /** Callback to remove mobile platform from project */
  onRemoveMobile?: () => Promise<void>
  /** Conversation ID for workflow triggers (deploy) */
  conversationId?: number
  /** Prototype sketch pane (rendered when in prototype mode) */
  sketchPane?: ReactNode
  /** Whether sketch view is available */
  hasSketch?: boolean
  /** Whether sketch should be the default active view */
  defaultSketchView?: boolean
  /** Whether AI is currently drawing on the sketch */
  isAIDrawing?: boolean
  /** Current AI drawing action description */
  aiDrawingAction?: string | null
  /** Callback to reload sketch from saved state */
  onRefreshSketch?: () => void
  /** Controlled sketch view active state (for parent-controlled switching) */
  isSketchViewActive?: boolean
  /** Callback when sketch view active state changes */
  onSketchViewChange?: (active: boolean) => void
  /** Active workflow (project-level, from Workflow model) */
  activeWorkflow?: ActiveWorkflow | null
  onWorkflowStart?: (workflowName: string) => void
  onAbortWorkflow?: () => void
  onRetryWorkflow?: () => Promise<void>
  isAbortingWorkflow?: boolean
  isRetryingWorkflow?: boolean
  /** Task board */
  projectTasks?: import('@/types/conversations').ProjectTask[]
  isBoardViewActive?: boolean
  onBoardViewChange?: (active: boolean) => void
  onStartTask?: (taskId: number) => Promise<any>
  onMarkDoneTask?: (taskId: number) => Promise<boolean>
  onStopTask?: (taskId: number) => Promise<any>
  onDeleteTask?: (taskId: number) => Promise<boolean>
  onAcceptTask?: (taskId: number) => Promise<any>
  onDiscardTask?: (taskId: number) => Promise<any>
  onRequestChanges?: (task: { id: number; title: string }) => void
  codingWorkers?: Array<{ id: number; name: string; status: string; preview_url: string; active_task_id: number | null }>
  provisioningWorkers?: string[]
  autoRun?: boolean
  onToggleAutoRun?: () => Promise<any>
  parallelAgentsEnabled?: boolean
  parallelWorkerCapacity?: number
  onToggleParallelAgents?: (value: boolean) => void
  verificationEnabled?: boolean
  onToggleVerification?: (value: boolean) => void
  /** Callback to insert a prompt into the chat input */
  onInsertPrompt?: (prompt: string) => void
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}

const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : []

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null

const humanizeManagedAgentValue = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const managedAgentModelLabel = (model: unknown): string => {
  if (typeof model === 'string') return model
  const modelRecord = asRecord(model)
  return asString(modelRecord.id) || 'Model pending'
}

const managedAgentToolLabels = (tools: unknown): string[] =>
  asArray(tools).flatMap((tool) => {
    const toolRecord = asRecord(tool)
    if (toolRecord.enabled === false) return []

    const configLabels = asArray(toolRecord.configs)
      .map((config) => {
        const configRecord = asRecord(config)
        if (configRecord.enabled === false) return null
        return asString(configRecord.name)
      })
      .filter(Boolean) as string[]

    if (Array.isArray(toolRecord.configs)) return configLabels.map(humanizeManagedAgentValue)

    const name = asString(toolRecord.name) || asString(toolRecord.type)
    return name ? [humanizeManagedAgentValue(name)] : []
  })

const managedAgentNamedList = (items: unknown): string[] =>
  asArray(items).flatMap((item) => {
    if (typeof item === 'string') return [item]
    const itemRecord = asRecord(item)
    const name = asString(itemRecord.name) || asString(itemRecord.title) || asString(itemRecord.id)
    return name ? [name] : []
  })

const managedAgentStatusClass = (status?: string | null): string => {
  switch (status) {
    case 'deployed':
    case 'completed':
    case 'passed':
    case 'ready':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-300'
    case 'failed':
      return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300'
    case 'review_required':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300'
    case 'running':
    case 'deploying':
    case 'validating':
    case 'refreshing':
    case 'pending':
    case 'draft':
    case 'building':
      return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/70 dark:bg-blue-950/30 dark:text-blue-300'
    case 'skipped':
      return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300'
    default:
      return 'border-border bg-muted text-muted-foreground'
  }
}

const formatManagedAgentTimestamp = (value?: string | null): string | null => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

export function PreviewPane({ project, isProvisioning = false, activeTab, messages: _messages = [], historyEntries = [], historyPagy, processing = false, groupedTodos, renderOnlyActiveTab = false, visitedTabs, conversationMode = 'implementation', isSelectionModeActive = false, onElementSelected, selectedElement, iframeRef: externalIframeRef, mobileIframeRef: externalMobileIframeRef, isBrowserUseTesting = false, deviceView: controlledDeviceView, onDeviceViewChange, appType: controlledAppType, onAppTypeChange, iframeKey: controlledIframeKey, onRefresh, organization, onViewDeployedInHistory, isRewinding, onRewindingChange, liveCredentials, liveProductSpec, onRemoveMobile, conversationId, sketchPane, hasSketch = false, defaultSketchView = false, isAIDrawing = false, aiDrawingAction, onRefreshSketch, isSketchViewActive: controlledSketchViewActive, onSketchViewChange, activeWorkflow, onWorkflowStart, onAbortWorkflow, onRetryWorkflow, isAbortingWorkflow, isRetryingWorkflow, projectTasks = [], isBoardViewActive = false, onBoardViewChange, onStartTask, onMarkDoneTask, onStopTask, onDeleteTask, onAcceptTask, onDiscardTask, onRequestChanges, codingWorkers = [], provisioningWorkers = [], autoRun = false, onToggleAutoRun, parallelAgentsEnabled = true, parallelWorkerCapacity = 0, onToggleParallelAgents, verificationEnabled = true, onToggleVerification, onInsertPrompt }: PreviewPaneProps) {
  // If visitedTabs not provided, render based on renderOnlyActiveTab or show all
  const shouldRenderTab = (tabName: string) => {
    if (visitedTabs) return visitedTabs.has(tabName)
    if (renderOnlyActiveTab) return activeTab === tabName
    return true
  }
  const { t } = useTranslation('conversations')
  const isManagedAgentProject = project.project_type === 'managed_agent'
  const canUseSketch = hasSketch && !isManagedAgentProject
  const [isRefreshing, setIsRefreshing] = useState(false)
  // Internal state for when not controlled by parent
  const [internalIframeKey, setInternalIframeKey] = useState(0)
  const [internalDeviceView, setInternalDeviceView] = useState<'desktop' | 'mobile'>('desktop')
  // App type state: 'web' for React web app, 'mobile' for Expo mobile app
  const [internalAppType, setInternalAppType] = useState<'web' | 'mobile'>('web')
  // Code view toggle state
  const [isCodeViewActive, setIsCodeViewActive] = useState(false)
  // Database view toggle state
  const [isDatabaseViewActive, setIsDatabaseViewActive] = useState(false)
  // Sketch view toggle state - controlled by parent when prop provided, otherwise internal
  const [internalSketchViewActive, _setInternalSketchViewActive] = useState(defaultSketchView && canUseSketch)
  const requestedSketchViewActive = controlledSketchViewActive ?? internalSketchViewActive
  const isSketchViewActive = canUseSketch && requestedSketchViewActive
  const setIsSketchViewActive = useCallback((active: boolean) => {
    if (active && !canUseSketch) return
    if (controlledSketchViewActive === undefined) _setInternalSketchViewActive(active)
    onSketchViewChange?.(active)
  }, [canUseSketch, controlledSketchViewActive, onSketchViewChange])

  // Mount-once pattern: Excalidraw mounts on first sketch activation, stays mounted via CSS
  // Uses state (not ref) so toggling triggers a re-render to mount the sketch pane
  const [hasEverMountedSketch, setHasEverMountedSketch] = useState(isSketchViewActive)
  useEffect(() => {
    if (isSketchViewActive && !hasEverMountedSketch) setHasEverMountedSketch(true)
  }, [isSketchViewActive, hasEverMountedSketch])

  // Use controlled state if provided, otherwise use internal state
  const iframeKey = controlledIframeKey ?? internalIframeKey
  const deviceView = controlledDeviceView ?? internalDeviceView
  const setDeviceView = onDeviceViewChange ?? setInternalDeviceView
  const appType = controlledAppType ?? internalAppType
  const setAppType = onAppTypeChange ?? setInternalAppType


  // Derive mobile app URL from web URL (only available for Django stack projects with mobile_included)
  const mobileAppUrl = project.django_stack ? getMobileAppUrl(project.starter_app_url ?? undefined) : undefined
  const hasMobileApp = project.django_stack && project.mobile_included && Boolean(mobileAppUrl)

  // Use live credentials if available (from ActionCable), otherwise fall back to project props
  const effectivePassword = liveCredentials?.rails_app_password ?? project.rails_app_password
  const effectiveAdminEmail = liveCredentials?.rails_app_admin_email ?? project.rails_app_admin_email

  // Use live product spec if available (from ActionCable), otherwise fall back to project props
  const effectiveProductSpec = liveProductSpec ?? project.product_spec

  const [iframeInitialUrl, setIframeInitialUrl] = useState(project.starter_app_url || '')
  const [mobileIframeInitialUrl, setMobileIframeInitialUrl] = useState(mobileAppUrl || '')

  // Track previous controlled key to detect external refresh triggers
  const prevControlledKeyRef = useRef(controlledIframeKey)
  // Track URLs separately for web and mobile iframes
  const [trackedWebUrl, setTrackedWebUrl] = useState(project.starter_app_url || '')
  const [trackedMobileUrl, setTrackedMobileUrl] = useState(mobileAppUrl || '')
  const [isOnLoginPage, setIsOnLoginPage] = useState(false)
  const adminCredentialsSetupAttempted = useRef(false)
  const [isCredentialsPopoverOpen, setIsCredentialsPopoverOpen] = useState(false)
  const [isUrlBarExpanded, setIsUrlBarExpanded] = useState(false)
  const [discoveredRoutes, setDiscoveredRoutes] = useState<string[]>([])
  const [isRouteDropdownOpen, setIsRouteDropdownOpen] = useState(false)
  const [urlBarInputValue, setUrlBarInputValue] = useState('')
  const internalIframeRef = useRef<HTMLIFrameElement>(null)
  const internalMobileIframeRef = useRef<HTMLIFrameElement>(null)
  const iframeRef = externalIframeRef || internalIframeRef
  const mobileIframeRef = externalMobileIframeRef || internalMobileIframeRef
  const [copiedEmail, setCopiedEmail] = useState(false)
  const [copiedPassword, setCopiedPassword] = useState(false)
  const [copiedProductionEmail, setCopiedProductionEmail] = useState(false)
  const [copiedProductionPassword, setCopiedProductionPassword] = useState(false)
  const [showProductionPassword, setShowProductionPassword] = useState(false)
  const [showProductionCredentials, setShowProductionCredentials] = useState(false)
  const managedAgentDraft = project.managed_agent_draft
  const managedAgentDeployment = project.managed_agent_deployment
  const managedAgentSession = managedAgentDeployment?.latest_session
  const managedAgentEvalRun = managedAgentDeployment?.latest_eval_run
  const [managedAgentTestInput, setManagedAgentTestInput] = useState('')
  const [isManagedAgentTestStarting, setIsManagedAgentTestStarting] = useState(false)
  const [showAllManagedAgentEvalResults, setShowAllManagedAgentEvalResults] = useState(false)
  const isManagedAgentSessionRunning = managedAgentSession?.status === 'pending' || managedAgentSession?.status === 'running'
  const managedAgentDeploymentCommit = asString(asRecord(managedAgentDeployment?.manifest?._source).resolved_commit_sha)
  const managedAgentDraftCommit = managedAgentDraft?.source_commit_sha || null
  const managedAgentDraftHasDefinition = Boolean(
    managedAgentDraft &&
    (managedAgentDraft.status === 'ready' || managedAgentDraft.status === 'refreshing') &&
    (Object.keys(managedAgentDraft.manifest || {}).length > 0 || Boolean(managedAgentDraft.system_prompt))
  )
  const managedAgentDraftIsCurrent = Boolean(
    managedAgentDraftHasDefinition &&
    (!managedAgentDeploymentCommit || (managedAgentDraftCommit && managedAgentDraftCommit !== managedAgentDeploymentCommit))
  )
  const managedAgentDefinition = managedAgentDraftHasDefinition
    ? managedAgentDraft
    : managedAgentDeployment
  const managedAgentManifest = managedAgentDefinition?.manifest || {}
  const managedAgentConfig = asRecord(managedAgentManifest.agent)
  const managedAgentEnvironment = asRecord(managedAgentManifest.environment)
  const managedAgentSystemPrompt = asString(managedAgentDefinition?.system_prompt)
  const managedAgentName = asString(managedAgentConfig.name) || project.dev_instance_name || project.name
  const managedAgentDescription = asString(managedAgentConfig.description)
  const managedAgentModel = managedAgentModelLabel(managedAgentConfig.model)
  const managedAgentTools = managedAgentToolLabels(managedAgentConfig.tools)
  const managedAgentMcpServers = managedAgentNamedList(managedAgentConfig.mcp_servers)
  const managedAgentSkills = managedAgentNamedList(managedAgentConfig.skills)
  const managedAgentEnvironmentType = asString(managedAgentEnvironment.type)
  const managedAgentNetworkingType = asString(asRecord(managedAgentEnvironment.networking).type)
  const managedAgentDefinitionStatus = processing
    ? 'building'
    : managedAgentDraft?.status === 'refreshing'
      ? 'refreshing'
      : managedAgentDraft?.status === 'failed'
        ? 'failed'
        : managedAgentDraftIsCurrent || !managedAgentDeployment
          ? 'draft'
          : managedAgentDeployment.status
  const managedAgentDeploymentStatus = managedAgentDeployment?.status || 'not_deployed'
  const managedAgentEvalPassed = managedAgentEvalRun?.status === 'completed' &&
    managedAgentEvalRun.total_count > 0 &&
    managedAgentEvalRun.failed_count === 0 &&
    managedAgentEvalRun.review_required_count === 0 &&
    managedAgentEvalRun.skipped_count === 0
  const managedAgentEvalNeedsAttention = Boolean(managedAgentEvalRun) && !managedAgentEvalPassed && managedAgentEvalRun?.status !== 'pending' && managedAgentEvalRun?.status !== 'running'
  const managedAgentSortedEvalResults = useMemo(() => {
    const results = managedAgentEvalRun?.results || []
    return [...results].sort((a, b) => {
      const priority = (status?: string) => {
        if (status === 'failed') return 0
        if (status === 'review_required') return 1
        if (status === 'skipped') return 2
        if (status === 'passed') return 3
        return 4
      }

      return priority(a.status) - priority(b.status)
    })
  }, [managedAgentEvalRun?.results])
  const managedAgentVisibleEvalResults = showAllManagedAgentEvalResults ? managedAgentSortedEvalResults : managedAgentSortedEvalResults.slice(0, 4)
  const managedAgentHiddenEvalResultCount = Math.max((managedAgentEvalRun?.results.length || 0) - managedAgentVisibleEvalResults.length, 0)
  const managedAgentEvalCompletedCount = managedAgentEvalRun
    ? managedAgentEvalRun.passed_count + managedAgentEvalRun.failed_count + managedAgentEvalRun.review_required_count + managedAgentEvalRun.skipped_count
    : 0
  const managedAgentEvalStatusText = managedAgentEvalRun
    ? managedAgentEvalRun.status === 'failed'
      ? managedAgentEvalCompletedCount > 0 && managedAgentEvalRun.total_count > 0
        ? `Failed after ${managedAgentEvalCompletedCount}/${managedAgentEvalRun.total_count}`
        : 'Failed'
      : managedAgentEvalPassed
      ? `${managedAgentEvalRun.total_count} passed`
      : managedAgentEvalRun.status === 'running' || managedAgentEvalRun.status === 'pending'
        ? `${managedAgentEvalCompletedCount}/${managedAgentEvalRun.total_count || '?'} running`
      : `${managedAgentEvalRun.failed_count + managedAgentEvalRun.review_required_count + managedAgentEvalRun.skipped_count} need attention`
    : 'Not run'
  const managedAgentEvalProgressText = managedAgentEvalRun
    ? managedAgentEvalRun.status === 'running' || managedAgentEvalRun.status === 'pending'
      ? `${managedAgentEvalCompletedCount}/${managedAgentEvalRun.total_count || '?'} complete`
      : `${managedAgentEvalCompletedCount}/${managedAgentEvalRun.total_count} complete`
    : 'Not run'
  const canStartManagedAgentTest = managedAgentDeployment?.status === 'deployed' && managedAgentTestInput.trim().length > 0 && !isManagedAgentTestStarting && !isManagedAgentSessionRunning
  const supportsAppPreview = project.capabilities.app_preview
  const supportsLiveEnvironment = project.capabilities.live_environment
  const recentAgentMessages = useMemo(
    () => {
      if (!isManagedAgentProject) return []

      return _messages
        .filter((message) => {
          if (!message.content?.trim()) return false

          // PM and builder replies belong in chat. The managed-agent preview
          // should only mirror operational logs and errors.
          if (message.source === 'discussion' || message.source === 'prototype') return false

          return message.message_type === 'task_log' || message.message_type === 'processing_error'
        })
        .slice(-3)
    },
    [_messages, isManagedAgentProject]
  )
  const managedAgentActivity = useMemo(() => {
    if (!isManagedAgentProject) return []

    const activity: Array<{ key: string; title: string; detail?: string | null; status?: string; at?: string | null }> = []

    if (managedAgentDraft) {
      activity.push({
        key: `draft-${managedAgentDraft.id}`,
        title: managedAgentDraft.status === 'ready' ? 'Draft updated' : managedAgentDraft.status === 'failed' ? 'Draft validation failed' : 'Draft refresh running',
        detail: managedAgentDraft.manifest_errors?.[0] || (managedAgentDraft.source_commit_sha ? `Commit ${managedAgentDraft.source_commit_sha.slice(0, 7)}` : null),
        status: managedAgentDraft.status,
        at: managedAgentDraft.refreshed_at || managedAgentDraft.updated_at,
      })
    }

    if (managedAgentDeployment) {
      activity.push({
        key: `deployment-${managedAgentDeployment.id}`,
        title: managedAgentDeployment.status === 'deployed' ? 'Deployment completed' : managedAgentDeployment.status === 'failed' ? 'Deployment failed' : 'Deployment in progress',
        detail: managedAgentDeployment.error_log || (managedAgentDeployment.anthropic_agent_version ? `Agent version ${managedAgentDeployment.anthropic_agent_version}` : null),
        status: managedAgentDeployment.status,
        at: managedAgentDeployment.deployed_at || managedAgentDeployment.completed_at || managedAgentDeployment.started_at || managedAgentDeployment.created_at,
      })
    }

    if (managedAgentEvalRun) {
      activity.push({
        key: `eval-${managedAgentEvalRun.id}`,
        title: managedAgentEvalRun.status === 'completed' ? 'Eval suite completed' : managedAgentEvalRun.status === 'failed' ? 'Eval suite failed' : 'Eval suite running',
        detail: managedAgentEvalRun.total_count > 0
          ? `${managedAgentEvalCompletedCount}/${managedAgentEvalRun.total_count} checks completed`
          : 'No behavior checks completed',
        status: managedAgentEvalPassed ? 'passed' : managedAgentEvalRun.status,
        at: managedAgentEvalRun.completed_at || managedAgentEvalRun.started_at || managedAgentEvalRun.created_at,
      })
    }

    if (managedAgentSession) {
      activity.push({
        key: `session-${managedAgentSession.id}`,
        title: managedAgentSession.status === 'completed' ? 'Live test completed' : managedAgentSession.status === 'failed' ? 'Live test failed' : 'Live test running',
        detail: managedAgentSession.input,
        status: managedAgentSession.status,
        at: managedAgentSession.completed_at || managedAgentSession.started_at || managedAgentSession.created_at,
      })
    }

    recentAgentMessages.forEach((message) => {
      activity.push({
        key: `message-${message.id}`,
        title: message.message_type === 'processing_error' ? 'Build error' : 'Build activity',
        detail: message.content,
        status: message.message_type === 'processing_error' ? 'failed' : 'running',
        at: message.created_at,
      })
    })

    return activity
      .sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime())
      .slice(0, 5)
  }, [isManagedAgentProject, managedAgentDraft, managedAgentDeployment, managedAgentEvalRun, managedAgentEvalCompletedCount, managedAgentEvalPassed, managedAgentSession, recentAgentMessages])
  const [isGeneratingCredentials, setIsGeneratingCredentials] = useState(false)
  const [showDeployDialog, setShowDeployDialog] = useState(false)
  const [isRedeployment, setIsRedeployment] = useState(false)
  const [showQRDialog, setShowQRDialog] = useState(false)
  const [tunnelUrl, setTunnelUrl] = useState<string | null>(null)
  const [tunnelUrlLoading, setTunnelUrlLoading] = useState(false)
  const [tunnelUrlError, setTunnelUrlError] = useState<string | null>(null)
  const [hintsHidden, setHintsHidden] = useState(() => {
    // Check if user has chosen to hide hints for today
    const hiddenUntil = localStorage.getItem('cayuHintsHiddenUntil')
    return hiddenUntil ? parseInt(hiddenUntil, 10) > Date.now() : false
  })
  const [shouldShowHints, setShouldShowHints] = useState(false)
  const [isInstanceStarting, setIsInstanceStarting] = useState(
    project.runtime_state === 'starting'
  )
  const [isStarting, setIsStarting] = useState(false)
  const [isWarmingUp, setIsWarmingUp] = useState(() => {
    // On initial load, check if instance just started (within last 2 min) and app isn't ready
    if (project.runtime_state !== 'running' || project.app_ready) return false
    try {
      const progress = project.setup_progress ? JSON.parse(project.setup_progress) : null
      if (!progress?.completed_at) return false
      const completedAt = new Date(progress.completed_at).getTime()
      return Date.now() - completedAt < WARMING_UP_TIMEOUT
    } catch { return false }
  })
  const warmingUpTimeoutRef = useRef<number | null>(null)
  const pollIntervalRef = useRef<number | null>(null)
  const instancePollIntervalRef = useRef<number | null>(null)

  const isWorkflowInProgress = checkWorkflowInProgress(activeWorkflow)
  const hasTaskBuilding = projectTasks.some(t => t.status === 'in_progress')

  // Track if we should show hints during this processing session
  // Show during implementation/planning mode OR during background task builds
  useEffect(() => {
    const isBuildActive = (processing || isWorkflowInProgress) && (conversationMode === 'implementation' || conversationMode === 'planning')
    if (isBuildActive || hasTaskBuilding) {
      setShouldShowHints(true)
      // Switch away from canvas so the hints overlay renders
      if (isSketchViewActive && !hasTaskBuilding) {
        setIsSketchViewActive(false)
      }
    } else {
      setShouldShowHints(false)
    }
  }, [processing, isWorkflowInProgress, conversationMode, hasTaskBuilding])

  // Track if browser-use testing was used during this processing session
  // Once true, stays true until processing ends (prevents user interaction between browser_execute calls)
  const [browserUseUsedThisSession, setBrowserUseUsedThisSession] = useState(false)
  const prevProcessingRef = useRef(processing)
  const prevHasTaskBuildingRef = useRef(hasTaskBuilding)
  const prevAppTypeRef = useRef(appType)

  const isActiveSession = processing || hasTaskBuilding

  useEffect(() => {
    // Reset when new processing or task-build session starts (false → true transition)
    if (processing && !prevProcessingRef.current) {
      setBrowserUseUsedThisSession(false)
    }
    if (hasTaskBuilding && !prevHasTaskBuildingRef.current) {
      setBrowserUseUsedThisSession(false)
    }

    if (isBrowserUseTesting && isActiveSession) {
      // Browser testing started during processing or task build - remember it
      setBrowserUseUsedThisSession(true)
    }

    prevProcessingRef.current = processing
    prevHasTaskBuildingRef.current = hasTaskBuilding
  }, [isBrowserUseTesting, processing, hasTaskBuilding, isActiveSession])

  // Show browser-use overlay if testing is active OR was used during this processing/task-build session
  const showBrowserUseOverlay = isActiveSession && browserUseUsedThisSession

  // Construct Metro URL directly from dev instance name (no API call needed)
  useEffect(() => {
    if (showQRDialog && project.dev_instance_name && project.django_stack) {
      // Construct Expo URL using nginx proxy subdomain, on the same sandbox base as the preview
      // Format: exp://hostname:443 for HTTPS connection via nginx
      const previewHost = project.starter_app_url ? new URL(project.starter_app_url).hostname : ''
      const sandboxBase = previewHost.startsWith(`${project.dev_instance_name}.`)
        ? previewHost.slice(project.dev_instance_name.length + 1)
        : 'sandbox.cayu.app'
      const metroUrl = `exp://metro-${project.dev_instance_name}.${sandboxBase}:443`
      setTunnelUrl(metroUrl)
      setTunnelUrlLoading(false)
      setTunnelUrlError(null)
    }
  }, [showQRDialog, project.dev_instance_name, project.django_stack, project.starter_app_url])

  // Computed: is hints panel currently visible?
  // Show during processing or workflow (unless hidden by user or browser-use)
  const isHintsPanelVisible = (processing || isWorkflowInProgress || hasTaskBuilding) && !hintsHidden && shouldShowHints && !showBrowserUseOverlay

  // Status message for hints panel
  const hintsStatusMessage = t('hints.statusMessage')

  // Fallback poll for deployment status (WebSocket delivers instant updates,
  // this is only a safety net if the connection drops)
  useEffect(() => {
    if (project.production_status === 'deploying') {
      pollIntervalRef.current = window.setInterval(() => {
        router.reload({ only: ['project'] })
      }, 60000)
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [project.production_status])

  // Poll for instance status when starting (fallback — cable handles real-time updates)
  const startingTimeoutRef = useRef<number | null>(null)
  useEffect(() => {
    if (isInstanceStarting) {
      instancePollIntervalRef.current = window.setInterval(() => {
        router.reload({ only: ['project'] })
      }, 10000) // Poll every 10 seconds

      // Safety net: force full reload after 5 minutes to re-sync state
      startingTimeoutRef.current = window.setTimeout(() => {
        router.reload()
      }, 300000)
    } else {
      if (instancePollIntervalRef.current) {
        clearInterval(instancePollIntervalRef.current)
        instancePollIntervalRef.current = null
      }
      if (startingTimeoutRef.current) {
        clearTimeout(startingTimeoutRef.current)
        startingTimeoutRef.current = null
      }
    }

    return () => {
      if (instancePollIntervalRef.current) {
        clearInterval(instancePollIntervalRef.current)
      }
      if (startingTimeoutRef.current) {
        clearTimeout(startingTimeoutRef.current)
      }
    }
  }, [isInstanceStarting])

  // Update starting/warming up state when project changes
  useEffect(() => {
    const wasStarting = isInstanceStarting
    const isNowRunning = project.runtime_state === 'running'
    setIsInstanceStarting(project.runtime_state === 'starting')

    // Transition: starting → running = enter warming up state
    if (wasStarting && isNowRunning && !project.app_ready) {
      setIsWarmingUp(true)
      setIsStarting(false)
      // Fallback: stop warming up after 2 min even if no cable event
      warmingUpTimeoutRef.current = window.setTimeout(() => {
        setIsWarmingUp(false)
      }, WARMING_UP_TIMEOUT)
    }
  }, [project.runtime_state])

  // Transition: provisioning → active = enter warming up state
  // (env var sync restarts Django ~30s after activation)
  const prevProvisioningRef = useRef(isProvisioning)
  useEffect(() => {
    const wasProvisioning = prevProvisioningRef.current
    prevProvisioningRef.current = isProvisioning

    if (wasProvisioning && !isProvisioning && project.instance_status === 'active' && !project.app_ready) {
      setIsWarmingUp(true)
      warmingUpTimeoutRef.current = window.setTimeout(() => {
        setIsWarmingUp(false)
      }, WARMING_UP_TIMEOUT)
    }
  }, [isProvisioning, project.instance_status])

  // Clear warming up when app_ready arrives via cable
  useEffect(() => {
    if (project.app_ready && isWarmingUp) {
      setIsWarmingUp(false)
      if (warmingUpTimeoutRef.current) {
        clearTimeout(warmingUpTimeoutRef.current)
        warmingUpTimeoutRef.current = null
      }
    }
  }, [project.app_ready])

  // Set up fallback timeout on mount if initialized in warming up state
  useEffect(() => {
    if (isWarmingUp && !warmingUpTimeoutRef.current) {
      // Calculate remaining time from setup_progress.completed_at
      let remaining = WARMING_UP_TIMEOUT
      try {
        const progress = project.setup_progress ? JSON.parse(project.setup_progress) : null
        if (progress?.completed_at) {
          const elapsed = Date.now() - new Date(progress.completed_at).getTime()
          remaining = Math.max(0, WARMING_UP_TIMEOUT - elapsed)
        }
      } catch { /* use default */ }

      warmingUpTimeoutRef.current = window.setTimeout(() => {
        setIsWarmingUp(false)
      }, remaining)
    }

    return () => {
      if (warmingUpTimeoutRef.current) {
        clearTimeout(warmingUpTimeoutRef.current)
      }
    }
  }, [])

  // Reset iframe URLs when project.starter_app_url changes (e.g., switching projects)
  useEffect(() => {
    if (project.starter_app_url) {
      setIframeInitialUrl(project.starter_app_url)
      setTrackedWebUrl(project.starter_app_url)
    }
    if (mobileAppUrl) {
      setMobileIframeInitialUrl(mobileAppUrl)
      setTrackedMobileUrl(mobileAppUrl)
    }
  }, [project.starter_app_url, mobileAppUrl])

  // When parent triggers a refresh via controlled key change, update iframe URL to current page
  useEffect(() => {
    if (
      controlledIframeKey !== undefined &&
      controlledIframeKey !== prevControlledKeyRef.current
    ) {
      // Parent triggered a refresh - update URL to current tracked page (not landing page)
      setIframeInitialUrl(trackedWebUrl)
      setMobileIframeInitialUrl(trackedMobileUrl)
      prevControlledKeyRef.current = controlledIframeKey
    }
  }, [controlledIframeKey, trackedWebUrl, trackedMobileUrl])

  // Handle postMessage from iframe for element selection, navigation tracking, and login detection
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin for security - allow localhost in development and Cayu infrastructure
      const isLocalhost = event.origin.includes('localhost') || event.origin.includes('127.0.0.1')
      const isCayuDomain = event.origin.includes('cayu.app') ||
        (project.domain_purchase?.domain_name && event.origin.includes(project.domain_purchase.domain_name))
      const isCayuAi = event.origin.includes('cayu.ai')
      const isCayuFly = event.origin.includes('cayu-ai.fly.dev')  // Staging/production on Fly.io

      if (!isLocalhost && !isCayuDomain && !isCayuAi && !isCayuFly) {
        console.warn('[PreviewPane] Rejected message from untrusted origin:', event.origin)
        return
      }

      // Handle navigation messages - track current URL for refresh (but don't update iframe src)
      // Support both 'data' and 'payload' for backward compatibility
      const messageData = event.data.data ?? event.data.payload
      if (event.data.type === 'cayu:navigation' && messageData?.url) {
        // Construct full URL if it's just a path
        let fullUrl = messageData.url
        if (messageData.url.startsWith('/')) {
          fullUrl = event.origin + messageData.url
        }

        // Determine if message is from web or mobile iframe based on origin
        const webOrigin = project.starter_app_url ? new URL(project.starter_app_url).origin : null
        const mobileOrigin = mobileAppUrl ? new URL(mobileAppUrl).origin : null

        if (webOrigin && event.origin === webOrigin) {
          setTrackedWebUrl(fullUrl)
        } else if (mobileOrigin && event.origin === mobileOrigin) {
          setTrackedMobileUrl(fullUrl)
        }
      }

      // Handle route discovery messages from SDK
      if (event.data.type === 'cayu:routes' && Array.isArray(messageData?.routes) && messageData.routes.every((r: unknown) => typeof r === 'string')) {
        setDiscoveredRoutes(messageData.routes)
      }

      // Handle login page detection
      if (event.data?.type === 'cayu:on-login-page') {
        setIsOnLoginPage(true)

        // Auto-fill credentials when login page detected
        // Determine which iframe sent the message by comparing origins
        const webOrigin = project.starter_app_url ? new URL(project.starter_app_url).origin : null
        const mobileOriginUrl = mobileAppUrl ? new URL(mobileAppUrl).origin : null
        const isFromMobile = mobileOriginUrl && event.origin === mobileOriginUrl

        const targetIframe = isFromMobile ? mobileIframeRef.current : iframeRef.current
        const targetOrigin = isFromMobile ? mobileOriginUrl : webOrigin

        if (targetIframe && effectivePassword && targetOrigin) {
          try {
            targetIframe.contentWindow?.postMessage(
              {
                type: 'cayu:fill-credentials',
                payload: {
                  email: effectiveAdminEmail || 'admin@example.com',
                  password: effectivePassword
                }
              },
              targetOrigin
            )
          } catch (error) {
            console.error('Failed to auto-fill credentials:', error)
          }
        } else if (!effectivePassword && !adminCredentialsSetupAttempted.current) {
          // Login page detected but no credentials — ask pilot to create them
          adminCredentialsSetupAttempted.current = true
          fetch(`/projects/${project.id}/setup_admin_credentials`, {
            method: 'POST',
            headers: {
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            }
          }).catch(() => {})  // Fire-and-forget — credentials arrive via ActionCable
        }
      } else if (event.data?.type === 'cayu:left-login-page') {
        setIsOnLoginPage(false)
      }

      // Handle element selected message
      if (event.data.type === 'ELEMENT_SELECTED' && event.data.data) {
        // Clear highlight in the OTHER iframe (the one that didn't send this message)
        const selectedFromMobile = event.data.data.appType === 'mobile'
        const otherIframe = selectedFromMobile
          ? iframeRef.current      // Clear web iframe
          : mobileIframeRef.current // Clear mobile iframe

        if (otherIframe?.contentWindow) {
          otherIframe.contentWindow.postMessage({ type: 'CLEAR_SELECTION' }, '*')
        }

        if (onElementSelected) {
          onElementSelected(event.data.data)
        }
      }

      // Handle viewport change from browser-use agent
      if (event.data.type === 'cayu:set-viewport' && event.data.data?.viewport) {
        const newViewport = event.data.data.viewport as 'desktop' | 'mobile'
        setDeviceView(newViewport)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [onElementSelected, project.domain_purchase?.domain_name, project.starter_app_url, mobileAppUrl])

  // Send selection mode state to the correct iframe based on appType
  // When appType changes while selection mode is active, disable on old iframe and enable on new one
  useEffect(() => {
    const prevAppType = prevAppTypeRef.current
    const appTypeChanged = prevAppType !== appType

    // If appType changed while selection mode is active, disable on old iframe
    if (appTypeChanged && isSelectionModeActive) {
      const oldIframe = prevAppType === 'mobile'
        ? mobileIframeRef.current
        : iframeRef.current

      if (oldIframe?.contentWindow) {
        oldIframe.contentWindow.postMessage({ type: 'DISABLE_SELECTION_MODE' }, '*')
      }
    }

    // Send to current target iframe
    const targetIframe = appType === 'mobile'
      ? mobileIframeRef.current
      : iframeRef.current

    if (targetIframe?.contentWindow) {
      const message = {
        type: isSelectionModeActive ? 'ENABLE_SELECTION_MODE' : 'DISABLE_SELECTION_MODE'
      }
      targetIframe.contentWindow.postMessage(message, '*')
    }

    prevAppTypeRef.current = appType
  }, [isSelectionModeActive, appType])

  // Send clear selection message when user clears the selected element
  useEffect(() => {
    const targetIframe = appType === 'mobile'
      ? mobileIframeRef.current
      : iframeRef.current

    if (!targetIframe?.contentWindow) return

    // When selectedElement becomes null after being set, clear the highlight in iframe
    if (selectedElement === null) {
      targetIframe.contentWindow.postMessage({ type: 'CLEAR_SELECTION' }, '*')
    }
  }, [selectedElement, appType])

  // Memoize ReactMarkdown components to preserve CollapsibleSection state across re-renders
  const markdownComponents = useMemo(() => ({
    section: ({ children }: { children?: ReactNode }) => {
      const childArray = Array.isArray(children) ? children : [children]
      const firstChild = childArray[0] as React.ReactElement | undefined
      const isH2Section = firstChild?.type === 'h2' ||
        ((firstChild?.props as any)?.node?.tagName === 'h2')

      if (isH2Section) {
        return <CollapsibleSection>{children}</CollapsibleSection>
      }
      return <section>{children}</section>
    }
  }), [])

  const handleRefresh = () => {
    setIsRefreshing(true)
    // Update iframe URLs to tracked URLs before remounting
    setIframeInitialUrl(trackedWebUrl)
    setMobileIframeInitialUrl(trackedMobileUrl)
    // Use parent's onRefresh if controlled, otherwise use internal state
    if (onRefresh) {
      onRefresh()
    } else {
      setInternalIframeKey(prev => prev + 1)
    }
    setTimeout(() => setIsRefreshing(false), 1000)
  }

  const handleNavigateIframe = useCallback((path: string) => {
    const targetIframe = iframeRef.current
    const targetUrl = project.starter_app_url
    if (!targetIframe || !targetUrl) return

    try {
      const targetOrigin = new URL(targetUrl).origin
      targetIframe.contentWindow?.postMessage(
        { type: 'cayu:navigate-to', data: { path } },
        targetOrigin
      )
    } catch (e) {
      console.error('[PreviewPane] Navigate failed:', e)
    }
  }, [project.starter_app_url])

  const currentPath = useMemo(() => {
    try {
      return trackedWebUrl ? new URL(trackedWebUrl).pathname : '/'
    } catch {
      return '/'
    }
  }, [trackedWebUrl])

  // Keep URL bar input in sync with iframe navigation
  useEffect(() => {
    if (isUrlBarExpanded && !isRouteDropdownOpen) {
      setUrlBarInputValue(currentPath)
    }
  }, [currentPath, isUrlBarExpanded, isRouteDropdownOpen])

  const handleFillCredentials = () => {
    if (!effectivePassword) {
      return
    }

    // Target the correct iframe based on current app type
    const targetIframe = appType === 'mobile' ? mobileIframeRef.current : iframeRef.current
    const targetUrl = appType === 'mobile' ? mobileAppUrl : project.starter_app_url

    if (!targetIframe || !targetUrl) {
      return
    }

    try {
      const targetOrigin = new URL(targetUrl).origin

      targetIframe.contentWindow?.postMessage(
        {
          type: 'cayu:fill-credentials',
          payload: {
            email: effectiveAdminEmail || 'admin@example.com',
            password: effectivePassword
          }
        },
        targetOrigin
      )

      setIsCredentialsPopoverOpen(false)
    } catch (error) {
      console.error('Failed to send credentials:', error)
    }
  }

  const handleDontShowHintsToday = () => {
    // Hide hints for 24 hours
    const hiddenUntil = Date.now() + 24 * 60 * 60 * 1000
    localStorage.setItem('cayuHintsHiddenUntil', hiddenUntil.toString())
    setHintsHidden(true)
  }

  const handleFullscreen = () => {
    const urlToOpen = appType === 'mobile' ? mobileAppUrl : project.starter_app_url
    if (urlToOpen) {
      window.open(urlToOpen, '_blank')
    }
  }

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(effectiveAdminEmail || 'admin@example.com')
    setCopiedEmail(true)
    setTimeout(() => setCopiedEmail(false), 2000)
  }

  const handleCopyPassword = () => {
    if (effectivePassword) {
      navigator.clipboard.writeText(effectivePassword)
      setCopiedPassword(true)
      setTimeout(() => setCopiedPassword(false), 2000)
    }
  }

  const handleCopyProductionEmail = () => {
    if (project.production_user_email) {
      navigator.clipboard.writeText(project.production_user_email)
      setCopiedProductionEmail(true)
      setTimeout(() => setCopiedProductionEmail(false), 2000)
    }
  }

  const handleCopyProductionPassword = () => {
    if (project.production_user_password) {
      navigator.clipboard.writeText(project.production_user_password)
      setCopiedProductionPassword(true)
      setTimeout(() => setCopiedProductionPassword(false), 2000)
    }
  }

  const handleGenerateCredentials = async () => {
    setIsGeneratingCredentials(true)
    try {
      const response = await fetch(`/projects/${project.id}/generate_production_credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })
      if (response.ok) {
        router.reload({ only: ['project'] })
      } else {
        const data = await response.json()
        alert(t('live.credentials.generateFailed', { error: data.error }))
      }
    } finally {
      setIsGeneratingCredentials(false)
    }
  }

  // Deployment handlers
  const isSuspendedNoCredits = project.production_status === 'suspended_no_credits'

  const handleDeployClick = () => {
    setIsRedeployment(false)
    setShowDeployDialog(true)
  }

  const handleRedeployClick = () => {
    setIsRedeployment(true)
    setShowDeployDialog(true)
  }

  const handleRestartLiveClick = () => {
    // Restart suspended project
    setIsRedeployment(false)
    setShowDeployDialog(true)
  }

  const handleConfirmDeploy = async () => {
    setShowDeployDialog(false)

    try {
      // Django projects use the deploy workflow (preflight checks + deploy + verify)
      if (project.django_stack && conversationId) {
        const response = await fetch(
          `/projects/${project.id}/features/${conversationId}/workflow/start`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            },
            body: JSON.stringify({ name: 'deploy_to_production' })
          }
        )

        if (response.ok) {
          onWorkflowStart?.('deploy_to_production')
        } else {
          const data = await response.json()
          alert(t('live.deployment.failedMessage', { error: data.error || data.errors?.[0] || t('live.deployment.unknownError') }))
        }

        return
      }

      // Rails projects use direct deploy
      const response = await fetch(`/projects/${project.id}/deploy_to_production`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      if (response.ok) {
        onWorkflowStart?.('deploy_to_production')
        router.reload({ only: ['project'] })
      } else {
        const data = await response.json()
        alert(t('live.deployment.failedMessage', { error: data.error || t('live.deployment.unknownError') }))
      }
    } catch (error) {
      console.error('Deployment error:', error)
      alert(t('live.deployment.startFailed'))
    }
  }

  const handleManagedAgentTest = async () => {
    const input = managedAgentTestInput.trim()
    if (!input || !managedAgentDeployment || managedAgentDeployment.status !== 'deployed') return

    setIsManagedAgentTestStarting(true)

    try {
      const response = await fetch(`/projects/${project.id}/managed_agent_sessions`, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          deployment_id: managedAgentDeployment.id,
          input
        })
      })

      if (response.ok) {
        setManagedAgentTestInput('')
        router.reload({ only: ['project'] })
      } else {
        const data = await response.json()
        alert(data.error || 'Failed to start Managed Agent test.')
      }
    } catch (error) {
      console.error('Managed Agent test error:', error)
      alert('Failed to start Managed Agent test.')
    } finally {
      setIsManagedAgentTestStarting(false)
    }
  }

  // Format stopped time
  const formatStoppedTime = (stoppedAt: string) => {
    const stopped = new Date(stoppedAt)
    const now = new Date()
    const diffMs = now.getTime() - stopped.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffDays > 0) {
      return t('preview.states.paused.timeAgo.days', { count: diffDays })
    } else if (diffHours > 0) {
      return t('preview.states.paused.timeAgo.hours', { count: diffHours })
    } else {
      return t('preview.states.paused.timeAgo.lessThanHour')
    }
  }

  // Parse startup progress
  const getStartupProgress = () => {
    if (!project.setup_progress) {
      return { message: t('preview.states.starting.initializing') }
    }

    try {
      const progress = JSON.parse(project.setup_progress)
      return {
        message: progress.message || t('preview.states.starting.startingUp')
      }
    } catch {
      return { message: t('preview.states.starting.initializing') }
    }
  }

  // Handle start instance
  const handleStartInstance = async () => {
    setIsStarting(true)
    try {
      router.post(`/projects/${project.id}/start_instance`, {}, {
        preserveScroll: true,
        onSuccess: () => {
          setIsInstanceStarting(true)
        },
        onError: (errors) => {
          console.error('Failed to start instance:', errors)
          alert(t('preview.errors.startFailed'))
          setIsStarting(false)
        }
      })
    } catch (error) {
      console.error('Start instance error:', error)
      alert(t('preview.errors.startFailed'))
      setIsStarting(false)
    }
  }


  return (
    <div className="flex h-full flex-col bg-card">
      {/* Header with controls */}
      {activeTab === 'preview' && (project.starter_app_url || mobileAppUrl || hasSketch || isManagedAgentProject) && (
        <div className="border-b border-border px-3 py-1.5 bg-background">
          <div className="flex items-center justify-between">
            {/* Left side controls */}
            <div className="flex items-center gap-1.5 flex-1">

              {/* Sketch controls - only Refresh (reload from saved state) */}
              {!showBrowserUseOverlay && isSketchViewActive && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                      onClick={onRefreshSketch}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('preview.refresh')}</TooltipContent>
                </Tooltip>
              )}

              {/* Iframe controls - Credentials, Refresh, Open in new tab */}
              {!showBrowserUseOverlay && !isSketchViewActive && supportsAppPreview && (
                <>
                  {/* Credentials Popover - Only shows on login page for Rails projects (starter-base > 0.0.3) */}
                  {effectivePassword && (!project.supports_login_detection || isOnLoginPage) && (
                    <Popover open={isCredentialsPopoverOpen} onOpenChange={setIsCredentialsPopoverOpen}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <PopoverTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                            >
                              <KeyRound className="h-4 w-4 animate-shimmer" strokeWidth={2.5} />
                            </Button>
                          </PopoverTrigger>
                        </TooltipTrigger>
                        <TooltipContent>{t('preview.credentials.viewCredentials')}</TooltipContent>
                      </Tooltip>
                      <PopoverContent className="w-80" align="start">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-foreground">
                              {t('preview.credentials.title')}
                            </div>
                            <PopoverClose asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 p-0 hover:bg-muted"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </PopoverClose>
                          </div>

                          {/* Email */}
                          <div>
                            <div className="text-xs font-medium text-muted-foreground mb-1">
                              {t('preview.credentials.email')}
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-mono bg-muted px-2.5 py-1.5 rounded border flex-1">
                                {effectiveAdminEmail || 'admin@example.com'}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0"
                                onClick={handleCopyEmail}
                                title={t('preview.credentials.copyEmail')}
                              >
                                {copiedEmail ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Password */}
                          <div>
                            <div className="text-xs font-medium text-muted-foreground mb-1">
                              {t('live.credentials.password')}
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-mono bg-muted px-2.5 py-1.5 rounded border flex-1">
                                {effectivePassword}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0"
                                onClick={handleCopyPassword}
                                title={t('preview.credentials.copyPassword')}
                              >
                                {copiedPassword ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Auto-fill Button */}
                          <div className="pt-3 border-t border-border">
                            <Button
                              onClick={handleFillCredentials}
                              className="w-full"
                              size="sm"
                            >
                              {t('preview.credentials.autoFill')}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}

                  {/* Refresh Button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('preview.refresh')}</TooltipContent>
                  </Tooltip>

                  {/* Open in new tab Button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                        onClick={handleFullscreen}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('preview.openInNewTab')}</TooltipContent>
                  </Tooltip>

                  {/* URL Bar - Collapsed globe icon / Expanded path input */}
                  <div className="h-4 w-px bg-border mx-0.5" />
                  {!isUrlBarExpanded ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          onClick={() => {
                            setIsUrlBarExpanded(true)
                            setUrlBarInputValue(currentPath)
                            setIsRouteDropdownOpen(true)
                          }}
                        >
                          <Globe className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('preview.urlBar.navigate')}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Popover open={isRouteDropdownOpen} onOpenChange={setIsRouteDropdownOpen}>
                      <Tooltip>
                        <PopoverTrigger asChild>
                          <TooltipTrigger asChild>
                            <div className={`flex items-center h-7 max-w-[200px] rounded-md border bg-accent/50 px-2 gap-1.5 cursor-pointer ${isRouteDropdownOpen ? 'border-primary' : 'border-border'}`}>
                              <Globe className={`h-3 w-3 shrink-0 ${isRouteDropdownOpen ? 'text-primary' : 'text-muted-foreground'}`} />
                              <input
                                type="text"
                                value={urlBarInputValue}
                                onChange={(e) => setUrlBarInputValue(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleNavigateIframe(urlBarInputValue)
                                    setIsRouteDropdownOpen(false)
                                    setIsUrlBarExpanded(false)
                                  } else if (e.key === 'Escape') {
                                    setIsUrlBarExpanded(false)
                                    setIsRouteDropdownOpen(false)
                                  }
                                }}
                                onFocus={() => setUrlBarInputValue(currentPath)}
                                className="bg-transparent text-xs font-mono text-foreground outline-none min-w-0 w-full truncate"
                                placeholder={t('preview.urlBar.placeholder')}
                                autoFocus
                              />
                              <div className={`shrink-0 p-0.5 ${isRouteDropdownOpen ? 'text-primary' : 'text-muted-foreground'}`}>
                                {isRouteDropdownOpen ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : (
                                  <ChevronDown className="h-3 w-3" />
                                )}
                              </div>
                            </div>
                          </TooltipTrigger>
                        </PopoverTrigger>
                        <TooltipContent className="max-w-[300px] font-mono text-xs">
                          {currentPath}
                        </TooltipContent>
                      </Tooltip>
                      <PopoverContent className="w-56 p-0" align="start" sideOffset={8}>
                        <div className="py-1.5">
                          <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">
                            {t('preview.urlBar.pages')}
                          </div>
                          {discoveredRoutes.length > 0 ? (
                            <>
                              {discoveredRoutes
                                .filter(route => !route.includes('$'))
                                .map((route) => (
                                  <button
                                    key={route}
                                    className={`flex items-center w-full px-3 py-1.5 text-xs font-mono hover:bg-accent ${currentPath === route ? 'bg-accent/60' : ''}`}
                                    onClick={() => {
                                      handleNavigateIframe(route)
                                      setIsRouteDropdownOpen(false)
                                      setIsUrlBarExpanded(false)
                                    }}
                                  >
                                    <span className="truncate">{route}</span>
                                    {currentPath === route && (
                                      <Check className="h-3 w-3 ml-auto shrink-0 text-primary" />
                                    )}
                                  </button>
                                ))}
                              {discoveredRoutes.some(route => route.includes('$')) && (
                                <>
                                  <div className="h-px bg-border mx-2 my-1" />
                                  {discoveredRoutes
                                    .filter(route => route.includes('$'))
                                    .map((route) => (
                                      <div
                                        key={route}
                                        className="flex items-center w-full px-3 py-1.5 text-xs font-mono text-muted-foreground"
                                      >
                                        <span className="truncate">{route}</span>
                                        <span className="ml-auto shrink-0 text-[10px] italic">{t('preview.urlBar.dynamic')}</span>
                                      </div>
                                    ))}
                                </>
                              )}
                            </>
                          ) : (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              {t('preview.urlBar.placeholder')}
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </>
              )}
            </div>

            {/* Center - View mode toggle */}
            {(
              <div className="flex items-center justify-center gap-1">
                {isManagedAgentProject && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={!isBoardViewActive && !isSketchViewActive && !isCodeViewActive && !isDatabaseViewActive ? "secondary" : "ghost"}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          onClick={() => {
                            setIsSketchViewActive(false)
                            setIsCodeViewActive(false)
                            setIsDatabaseViewActive(false)
                            onBoardViewChange?.(false)
                          }}
                        >
                          <Bot className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('preview.managedAgent.title')}</TooltipContent>
                    </Tooltip>
                    <div className="h-4 w-px bg-border" />
                  </>
                )}
                {/* Board toggle */}
                {onBoardViewChange && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={isBoardViewActive && !isSketchViewActive && !isCodeViewActive && !isDatabaseViewActive ? "secondary" : "ghost"}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          onClick={() => {
                            onBoardViewChange(!isBoardViewActive)
                            if (!isBoardViewActive) {
                              setIsSketchViewActive(false)
                              setIsCodeViewActive(false)
                              setIsDatabaseViewActive(false)
                            }
                          }}
                        >
                          <SquareKanban className={`!h-[18px] !w-[18px] ${!isBoardViewActive && projectTasks.some(t => t.status === 'review') ? 'text-amber-500 animate-shimmer' : ''}`} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('taskBoard.title')}</TooltipContent>
                    </Tooltip>
                    <div className="h-4 w-px bg-border" />
                  </>
                )}
                {/* Sketch toggle */}
                {canUseSketch && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={isSketchViewActive && !isCodeViewActive && !isDatabaseViewActive ? "secondary" : "ghost"}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          onClick={() => {
                            setIsSketchViewActive(true)
                            setIsCodeViewActive(false)
                            setIsDatabaseViewActive(false)
                            onBoardViewChange?.(false)
                          }}
                        >
                          <DraftingCompass className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('preview.prototype')}</TooltipContent>
                    </Tooltip>
                    <div className="h-4 w-px bg-border" />
                  </>
                )}
                {supportsAppPreview && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={appType === 'web' && deviceView === 'desktop' && !isCodeViewActive && !isDatabaseViewActive && !isSketchViewActive && !isBoardViewActive ? "secondary" : "ghost"}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          onClick={() => {
                            setIsSketchViewActive(false)
                            setIsCodeViewActive(false)
                            setIsDatabaseViewActive(false)
                            onBoardViewChange?.(false)
                            setAppType('web')
                            setDeviceView('desktop')
                          }}
                        >
                          <Monitor className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('preview.desktop')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={appType === 'web' && deviceView === 'mobile' && !isCodeViewActive && !isDatabaseViewActive && !isSketchViewActive && !isBoardViewActive ? "secondary" : "ghost"}
                          className="hidden md:inline-flex h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          onClick={() => {
                            setIsSketchViewActive(false)
                            setIsCodeViewActive(false)
                            setIsDatabaseViewActive(false)
                            onBoardViewChange?.(false)
                            setAppType('web')
                            setDeviceView('mobile')
                          }}
                        >
                          <MonitorSmartphone className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('preview.responsive')}</TooltipContent>
                    </Tooltip>
                    {hasMobileApp && (
                      <>
                        <div className="h-4 w-px bg-border" />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant={appType === 'mobile' && !isCodeViewActive && !isDatabaseViewActive && !isSketchViewActive && !isBoardViewActive ? "secondary" : "ghost"}
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                              onClick={() => {
                                setIsSketchViewActive(false)
                                setIsCodeViewActive(false)
                                setIsDatabaseViewActive(false)
                                onBoardViewChange?.(false)
                                setAppType('mobile')
                                setDeviceView('mobile')
                              }}
                            >
                              <Smartphone className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t('preview.mobileApp')}</TooltipContent>
                        </Tooltip>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Right side controls */}
            <div className="flex items-center gap-1.5 flex-1 justify-end">
              {!showBrowserUseOverlay && (
              <>
                {/* Database toggle - Django stack only */}
                {project.django_stack && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant={isDatabaseViewActive ? "secondary" : "ghost"}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                        onClick={() => {
                          setIsSketchViewActive(false)
                          setIsDatabaseViewActive(!isDatabaseViewActive)
                          if (!isDatabaseViewActive) setIsCodeViewActive(false)
                          onBoardViewChange?.(false)
                        }}
                      >
                        <Database className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('tabs.database')}</TooltipContent>
                  </Tooltip>
                )}
                {/* Code toggle */}
                {!isManagedAgentProject && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={isCodeViewActive ? "secondary" : "ghost"}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                          onClick={() => {
                            setIsSketchViewActive(false)
                            onBoardViewChange?.(false)
                            setIsCodeViewActive(!isCodeViewActive)
                            if (!isCodeViewActive) setIsDatabaseViewActive(false)
                          }}
                        >
                          <Code2 className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('code.title')}</TooltipContent>
                    </Tooltip>
                    <div className="h-4 w-px bg-border" />
                  </>
                )}
                {!isProvisioning && <SandboxSettingsPopover project={project} organization={organization} isBusy={processing || isWorkflowInProgress || hasTaskBuilding} />}
              </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Provisioning overlay — shown instead of all tab content while sandbox is being created */}
      {isProvisioning && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-muted/30">
          {(project.instance_status as string) === 'setup_failed' ? (
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-destructive">{t('provisioning.failed.title')}</h3>
              <p className="text-sm text-muted-foreground">{t('provisioning.failed.description')}</p>
            </div>
          ) : (
            <>
              <div className="h-12 w-12 rounded-full border-4 border-muted animate-spin border-t-primary" />
              <div className="text-center space-y-1">
                <h3 className="text-base font-semibold">{t('provisioning.title')}</h3>
                <p className="text-xs text-muted-foreground">{t('provisioning.subtitle')}</p>
                {(() => {
                  if (!project.setup_progress) return null
                  try { const msg = JSON.parse(project.setup_progress)?.message; return msg ? <p className="text-xs text-muted-foreground/70">{msg}</p> : null } catch { return null }
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* Preview Content */}
      {!isProvisioning && shouldRenderTab('preview') && (
        <div className={`flex-1 overflow-hidden relative ${activeTab === 'preview' ? '' : 'hidden'}`}>
          {/* Sketch pane - mounts on first activation, stays mounted via CSS hidden */}
          {canUseSketch && hasEverMountedSketch && sketchPane && (
            <div className={`relative h-full ${isSketchViewActive ? '' : 'hidden'}`}>
              {sketchPane}
              {/* AI drawing status badge - canvas is in view mode (pan/zoom allowed, editing locked) */}
              {isAIDrawing && (
                <div className="absolute bottom-3 right-3 z-10 pointer-events-none bg-violet-600 text-white text-xs py-1.5 px-3 rounded-full shadow-lg flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                  <span>{aiDrawingAction || 'Cayu is using the canvas...'}</span>
                </div>
              )}
            </div>
          )}

          {/* Board view — shows when board toggle is active */}
          {isBoardViewActive && !isSketchViewActive && (
            <div className="absolute inset-0 bg-background overflow-hidden">
              <BoardView
                tasks={projectTasks}
                codingWorkers={codingWorkers}
                provisioningWorkers={provisioningWorkers}
                onStart={(id) => onStartTask?.(id) ?? Promise.resolve(null)}
                onMarkDone={(id) => onMarkDoneTask?.(id)}
                onStop={(id) => onStopTask?.(id)}
                onAccept={(id) => onAcceptTask?.(id) ?? Promise.resolve(null)}
                onDiscard={(id) => onDiscardTask?.(id) ?? Promise.resolve(null)}
                onRequestChanges={onRequestChanges}
                onDelete={(id) => { onDeleteTask?.(id) }}
                autoRun={autoRun}
                onToggleAutoRun={onToggleAutoRun}
                parallelAgentsEnabled={parallelAgentsEnabled}
                parallelWorkerCapacity={parallelWorkerCapacity}
                onToggleParallelAgents={onToggleParallelAgents}
                verificationEnabled={verificationEnabled}
                onToggleVerification={onToggleVerification}
                managedAgentProject={isManagedAgentProject}
              />
            </div>
          )}

          {/* Other views - hidden when sketch or board is active */}
          {isDatabaseViewActive && !isSketchViewActive && !isBoardViewActive && project.django_stack ? (
            <DatabaseTab project={project} onInsertPrompt={onInsertPrompt} />
          ) : isCodeViewActive && !isSketchViewActive && !isBoardViewActive ? (
            /* Code View - replaces iframe preview when active */
            <CodeTab project={project} onInsertPrompt={onInsertPrompt} />
          ) : (
            <div className={`absolute inset-0 ${isSketchViewActive || isBoardViewActive ? 'invisible' : ''}`}>
          {/* Hints overlay - rendered on top of iframe, not replacing it */}
          {isHintsPanelVisible && (
            <div className="absolute inset-0 z-30">
              <HintsDisplay
                onClose={() => setHintsHidden(true)}
                onDontShowToday={handleDontShowHintsToday}
                groupedTodos={groupedTodos}
                statusMessage={hintsStatusMessage}
                onOpenBoard={onBoardViewChange ? () => {
                  setHintsHidden(true)
                  onBoardViewChange(true)
                } : undefined}
              />
            </div>
          )}
          {/* Browser interaction overlay - blocks user clicks during verification */}
          {/* Stays visible for entire processing session once browser-use is used */}
          {showBrowserUseOverlay && (
            <div className="absolute inset-0 z-10 cursor-not-allowed">
              {/* Floating badge in bottom-right corner - minimal app coverage */}
              <div className="absolute bottom-3 right-3 bg-primary text-primary-foreground text-xs py-1.5 px-3 rounded-full shadow-lg flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-primary-foreground animate-pulse" />
                <span>{t('preview.states.interacting')}</span>
              </div>
            </div>
          )}
          {isManagedAgentProject && project.instance_status !== 'setup_failed' && project.runtime_state !== 'stopping' && !(project.runtime_state === 'stopped' && project.stopped_at) && !isInstanceStarting ? (
            <div className="h-full overflow-auto bg-background">
              <div className="mx-auto max-w-4xl p-6 space-y-6">
                <div className="rounded-md border bg-card p-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-blue-50 dark:bg-blue-950/30">
                        <Bot className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-semibold text-foreground">{managedAgentName}</h3>
                          <Badge variant="outline" className={managedAgentStatusClass(managedAgentDefinitionStatus)}>
                            {humanizeManagedAgentValue(managedAgentDefinitionStatus)}
                          </Badge>
                        </div>
                        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                          {managedAgentDescription || 'Describe what this agent should do in chat, then deploy and test it here.'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm md:min-w-64">
                      <div className="rounded border bg-muted/20 p-3">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Model</div>
                        <div className="mt-1 truncate font-medium text-foreground">{managedAgentModel}</div>
                      </div>
                      <div className="rounded border bg-muted/20 p-3">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tests</div>
                        <div className="mt-1 truncate font-medium text-foreground">
                          {managedAgentEvalStatusText}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
                  <div className="rounded-md border">
                    <div className="flex items-center gap-2 border-b px-4 py-3">
                      <BookOpen className="h-4 w-4 text-blue-600" />
                      <h4 className="text-sm font-semibold text-foreground">Instructions</h4>
                    </div>
                    <div className="space-y-3 p-4">
                      {managedAgentDraft?.status === 'failed' && managedAgentDraft.manifest_errors.length > 0 && (
                        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300">
                          <div className="font-medium">Draft needs attention</div>
                          <ul className="mt-2 list-disc space-y-1 pl-5">
                            {managedAgentDraft.manifest_errors.map((error) => (
                              <li key={error}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {managedAgentSystemPrompt ? (
                        <div className="max-h-72 overflow-auto rounded border bg-muted/20 p-3 text-sm leading-6 text-foreground">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {managedAgentSystemPrompt}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <div className="rounded border border-dashed p-4 text-sm text-muted-foreground">
                          The agent instructions will appear here after the builder saves the agent.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-md border">
                    <div className="flex items-center gap-2 border-b px-4 py-3">
                      <Wrench className="h-4 w-4 text-slate-600" />
                      <h4 className="text-sm font-semibold text-foreground">Capabilities</h4>
                    </div>
                    <div className="space-y-4 p-4 text-sm">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tools</div>
                        {managedAgentTools.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {managedAgentTools.map((tool) => (
                              <Badge key={tool} variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/70 dark:bg-blue-950/30 dark:text-blue-300">
                                {tool}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-2 text-muted-foreground">No tools configured.</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Connected Services</div>
                        {managedAgentMcpServers.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {managedAgentMcpServers.map((server) => (
                              <Badge key={server} variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-300">
                                {server}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-2 text-muted-foreground">No external services connected.</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Skills</div>
                        {managedAgentSkills.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {managedAgentSkills.map((skill) => (
                              <Badge key={skill} variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-2 text-muted-foreground">No skills added yet.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border">
                  <div className="flex items-center gap-2 border-b px-4 py-3">
                    {managedAgentDeployment?.status === 'deployed' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : managedAgentDeployment?.status === 'failed' ? (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    ) : managedAgentDeployment ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <h4 className="text-sm font-semibold text-foreground">Deployment</h4>
                    <Badge variant="outline" className={`ml-auto ${managedAgentStatusClass(managedAgentDeploymentStatus)}`}>
                      {humanizeManagedAgentValue(managedAgentDeploymentStatus)}
                    </Badge>
                  </div>
                  <div className="grid gap-3 p-4 text-sm md:grid-cols-3">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Environment</div>
                      <div className="mt-1 text-foreground">
                        {managedAgentEnvironmentType ? humanizeManagedAgentValue(managedAgentEnvironmentType) : 'Pending'}
                        {managedAgentNetworkingType ? `, ${humanizeManagedAgentValue(managedAgentNetworkingType)} network` : ''}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Release</div>
                      <div className="mt-1 text-foreground">
                        {managedAgentDeployment?.status === 'deployed' ? 'Live' : humanizeManagedAgentValue(managedAgentDeploymentStatus)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Last Deploy</div>
                      <div className="mt-1 text-foreground">
                        {formatManagedAgentTimestamp(managedAgentDeployment?.deployed_at) || 'Not deployed'}
                      </div>
                    </div>
                  </div>
                  {managedAgentDeployment?.error_log && (
                    <div className="border-t p-4">
                      <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300">
                        {managedAgentDeployment.error_log}
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-md border">
                  <div className="flex items-center gap-2 border-b px-4 py-3">
                    <TerminalSquare className="h-4 w-4 text-blue-600" />
                    <h4 className="text-sm font-semibold text-foreground">Live Test</h4>
                    {isManagedAgentSessionRunning && (
                      <Badge variant="outline" className={`ml-auto ${managedAgentStatusClass('running')}`}>
                        running
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-3 p-4">
                    <Textarea
                      value={managedAgentTestInput}
                      onChange={(event) => setManagedAgentTestInput(event.target.value)}
                      placeholder={managedAgentDeployment?.status === 'deployed' ? 'Send a test message to the deployed agent' : 'Deploy the agent before running a live test'}
                      disabled={managedAgentDeployment?.status !== 'deployed' || isManagedAgentTestStarting}
                      className="min-h-[96px] resize-y"
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleManagedAgentTest}
                        disabled={!canStartManagedAgentTest}
                        className="bg-blue-600 text-white hover:bg-blue-700"
                      >
                        {isManagedAgentTestStarting ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <PlayCircle className="mr-2 h-4 w-4" />
                        )}
                        Run Test
                      </Button>
                    </div>
                  </div>
                </div>

                {managedAgentSession && (
                  <div className="rounded-md border">
                    <div className="flex items-center gap-2 border-b px-4 py-3">
                      <TerminalSquare className="h-4 w-4 text-slate-600" />
                      <h4 className="text-sm font-semibold text-foreground">Latest Test Run</h4>
                      <Badge variant="outline" className={`ml-auto ${managedAgentStatusClass(managedAgentSession.status)}`}>
                        {humanizeManagedAgentValue(managedAgentSession.status)}
                      </Badge>
                    </div>
                    <div className="space-y-3 p-4">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Input</div>
                        <div className="mt-1 rounded border bg-muted/30 p-3 text-sm text-foreground">
                          {managedAgentSession.input}
                        </div>
                      </div>
                      {(managedAgentSession.output || managedAgentSession.error_log) && (
                        <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {managedAgentSession.output ? 'Output' : 'Error'}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap rounded border bg-muted/30 p-3 text-sm text-foreground">
                            {managedAgentSession.output || managedAgentSession.error_log}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {managedAgentEvalRun && (
                  <div className="rounded-md border">
                    <div className="flex items-center gap-2 border-b px-4 py-3">
                      {managedAgentEvalRun.status === 'running' || managedAgentEvalRun.status === 'pending' ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      ) : managedAgentEvalRun.status === 'failed' ? (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      ) : managedAgentEvalNeedsAttention ? (
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      )}
                      <h4 className="text-sm font-semibold text-foreground">Eval Suite</h4>
                      <Badge variant="outline" className={`ml-auto ${managedAgentStatusClass(managedAgentEvalPassed ? 'passed' : managedAgentEvalRun.status)}`}>
                        {managedAgentEvalRun.status === 'failed'
                          ? managedAgentEvalStatusText
                          : managedAgentEvalProgressText}
                      </Badge>
                    </div>
                    <div className="space-y-4 p-4">
                      <div className="grid gap-3 text-sm md:grid-cols-5">
                        <div className="rounded border bg-muted/30 p-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total</div>
                          <div className="mt-1 text-lg font-semibold text-foreground">{managedAgentEvalRun.total_count}</div>
                        </div>
                        <div className="rounded border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/70 dark:bg-emerald-950/30">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Passed</div>
                          <div className="mt-1 text-lg font-semibold text-emerald-700 dark:text-emerald-300">{managedAgentEvalRun.passed_count}</div>
                        </div>
                        <div className="rounded border border-red-200 bg-red-50 p-3 dark:border-red-900/70 dark:bg-red-950/30">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Failed</div>
                          <div className="mt-1 text-lg font-semibold text-red-700 dark:text-red-300">{managedAgentEvalRun.failed_count}</div>
                        </div>
                        <div className="rounded border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/70 dark:bg-amber-950/30">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Review</div>
                          <div className="mt-1 text-lg font-semibold text-amber-700 dark:text-amber-300">{managedAgentEvalRun.review_required_count}</div>
                        </div>
                        <div className="rounded border bg-slate-50 p-3 dark:bg-slate-900/40">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Skipped</div>
                          <div className="mt-1 text-lg font-semibold text-slate-700 dark:text-slate-300">{managedAgentEvalRun.skipped_count}</div>
                        </div>
                      </div>
                      {managedAgentEvalRun.error_log && (
                        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300">
                          {managedAgentEvalRun.error_log}
                        </div>
                      )}
                      {managedAgentVisibleEvalResults.length > 0 && (
                        <div className="space-y-2">
                          {managedAgentVisibleEvalResults.map((result, index) => (
                            <div key={`${result.name || 'case'}-${index}`} className="rounded border bg-muted/30 p-3 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-foreground">{result.name || `Case ${index + 1}`}</span>
                                <Badge variant="outline" className={`ml-auto ${managedAgentStatusClass(result.status)}`}>
                                  {humanizeManagedAgentValue(result.status || 'unknown')}
                                </Badge>
                              </div>
                              {result.reason && (
                                <div className="mt-2 whitespace-pre-wrap text-muted-foreground">{result.reason}</div>
                              )}
                            </div>
                          ))}
                          {managedAgentSortedEvalResults.length > 4 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={() => setShowAllManagedAgentEvalResults((value) => !value)}
                            >
                              {showAllManagedAgentEvalResults ? (
                                <>
                                  <ChevronUp className="mr-2 h-4 w-4" />
                                  Show fewer results
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="mr-2 h-4 w-4" />
                                  Show {managedAgentHiddenEvalResultCount} more result{managedAgentHiddenEvalResultCount === 1 ? '' : 's'}
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="rounded-md border">
                  <div className="flex items-center gap-2 border-b px-4 py-3">
                    <ListChecks className="h-4 w-4 text-emerald-600" />
                    <h4 className="text-sm font-semibold text-foreground">Behavior Checks</h4>
                  </div>
                  <div className="p-4 text-sm">
                    {managedAgentEvalRun ? (
                      <div className="flex flex-col gap-2 text-muted-foreground md:flex-row md:items-center md:justify-between">
                        <span>
                          Last suite: {managedAgentEvalRun.total_count} behavior check{managedAgentEvalRun.total_count === 1 ? '' : 's'}
                        </span>
                        <span>
                          {formatManagedAgentTimestamp(managedAgentEvalRun.completed_at || managedAgentEvalRun.started_at)}
                        </span>
                      </div>
                    ) : (
                      <div className="rounded border border-dashed p-4 text-muted-foreground">
                        Ask PM to deploy and run the eval suite when the agent is ready.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-md border">
                  <div className="flex items-center gap-2 border-b px-4 py-3">
                    <Settings2 className="h-4 w-4 text-slate-600" />
                    <h4 className="text-sm font-semibold text-foreground">Activity</h4>
                  </div>
                  <div className="space-y-3 p-4">
                    {managedAgentActivity.length > 0 ? (
                      managedAgentActivity.map((event) => (
                        <div key={event.key} className="rounded border bg-muted/20 p-3">
                          <div className="flex items-start gap-3">
                            <Badge variant="outline" className={managedAgentStatusClass(event.status)}>
                              {humanizeManagedAgentValue(event.status || 'info')}
                            </Badge>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                                <div className="font-medium text-foreground">{event.title}</div>
                                {formatManagedAgentTimestamp(event.at) && (
                                  <div className="text-xs text-muted-foreground">{formatManagedAgentTimestamp(event.at)}</div>
                                )}
                              </div>
                              {event.detail && (
                                <div className="mt-1 line-clamp-3 whitespace-pre-wrap text-sm text-muted-foreground">
                                  {event.detail}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground">
                        Agent activity will appear here after PM starts building, deploying, or testing.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : project.runtime_state === 'stopping' ? (
            // Stopping state - show spinner while EC2 is shutting down
            <div className="flex items-center justify-center h-full p-8 bg-white dark:bg-gray-900">
              <div className="text-center space-y-6 max-w-lg">
                <div className="mx-auto h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-yellow-600 dark:text-yellow-400 animate-spin" />
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {isManagedAgentProject ? t('preview.managedAgent.stopping') : t('preview.states.paused.stopping')}
                  </h3>
                </div>
              </div>
            </div>
          ) : project.runtime_state === 'stopped' && project.stopped_at ? (
            // Stopped state - show information and start button
            <div className="flex items-center justify-center h-full p-8 bg-white dark:bg-gray-900">
              <div className="text-center space-y-6 max-w-lg">
                <div className="mx-auto h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <Pause className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {isManagedAgentProject ? t('preview.managedAgent.pausedTitle') : t('preview.states.paused.title')}
                  </h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>
                      {isManagedAgentProject
                        ? t('preview.managedAgent.pausedDescription', { time: formatStoppedTime(project.stopped_at) })
                        : t('preview.states.paused.description', { time: formatStoppedTime(project.stopped_at) })}
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={handleStartInstance}
                  disabled={isStarting}
                  className="w-full max-w-xs mx-auto"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isManagedAgentProject ? t('preview.managedAgent.starting') : t('preview.states.starting.button')}
                    </>
                  ) : (
                    <>
                      <PlayCircle className="mr-2 h-5 w-5" />
                      {isManagedAgentProject ? t('preview.managedAgent.startButton') : t('preview.states.paused.startButton')}
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {isManagedAgentProject ? t('preview.managedAgent.startingHint') : t('preview.states.starting.hint')}
                </p>
              </div>
            </div>
          ) : isInstanceStarting ? (
            // Loading state - instance is starting up with progress
            (() => {
              const progress = getStartupProgress()
              return (
                <div className="flex items-center justify-center h-full p-8 bg-white dark:bg-gray-900">
                  <div className="text-center space-y-6 max-w-lg w-full">
                    <div className="mx-auto h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
                    </div>
                    <div className="space-y-3">
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                        {isManagedAgentProject ? t('preview.managedAgent.starting') : t('preview.states.starting.title')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {progress.message}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {isManagedAgentProject ? t('preview.managedAgent.startingHint') : t('preview.states.starting.hint')}
                    </p>
                  </div>
                </div>
              )
            })()
          ) : isWarmingUp ? (
            // Warming up state - EC2 running but app not ready yet
            <div className="flex items-center justify-center h-full p-8 bg-white dark:bg-gray-900">
              <div className="text-center space-y-6 max-w-lg w-full">
                <div className="mx-auto h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {isManagedAgentProject ? t('preview.managedAgent.warmingUp') : t('preview.states.warmingUp.title')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('preview.states.warmingUp.description')}
                  </p>
                </div>
              </div>
            </div>
          ) : project.instance_status === 'setup_failed' ? (
            // Error state - instance setup failed
            <div className="flex items-center justify-center h-full p-8 bg-white dark:bg-gray-900">
              <div className="text-center space-y-4 max-w-md">
                <div className="mx-auto h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm mb-1 text-gray-900 dark:text-gray-100">
                    {t('preview.states.failed.title')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('preview.states.failed.description')}
                  </p>
                </div>
              </div>
            </div>
          ) : (project.starter_app_url || mobileAppUrl) ? (
            <>
              {/* Web app iframe - visible when appType is 'web' */}
              {project.starter_app_url && (
                <div
                  className={`h-full w-full flex items-start justify-center overflow-auto ${deviceView === 'mobile' ? 'bg-gray-100 dark:bg-gray-900' : 'bg-white dark:bg-gray-900'} ${appType !== 'web' ? 'hidden' : ''}`}
                >
                  <div
                    className={
                      deviceView === 'mobile'
                        ? 'w-[393px] h-[min(852px,calc(100%-2rem))] shadow-sm mt-4 flex-shrink-0 bg-white'
                        : 'h-full w-full'
                    }
                  >
                    <iframe
                      ref={iframeRef}
                      key={`web-${iframeKey}`}
                      src={iframeInitialUrl}
                      className="h-full w-full border-0"
                      title={t('preview.iframeTitle')}
                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
                      allow="microphone; camera; geolocation; clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; payment; web-share"
                    />
                  </div>
                </div>
              )}

              {/* Mobile app iframe - visible when appType is 'mobile' */}
              {mobileAppUrl && (
                <div
                  className={`h-full w-full relative bg-gray-100 dark:bg-gray-900 ${appType !== 'mobile' ? 'hidden' : ''}`}
                >
                  <ScaledPhoneContainer>
                    <IPhoneFrame>
                      <IPhoneStatusBar />
                      <iframe
                        ref={mobileIframeRef}
                        key={`mobile-${iframeKey}`}
                        src={mobileIframeInitialUrl}
                        className="absolute left-0 right-0 w-full border-0 bg-black"
                        style={{ top: '48px', height: 'calc(100% - 48px)' }}
                        title={t('preview.mobileIframeTitle')}
                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
                        allow="microphone; camera; geolocation; clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; payment; web-share"
                      />
                    </IPhoneFrame>
                  </ScaledPhoneContainer>
                  <div className="absolute top-4 right-4">
                    <MobilePreviewSidePanel
                      onOpenQRDialog={() => setShowQRDialog(true)}
                      onRemoveMobile={onRemoveMobile}
                      isProcessing={processing}
                      compact
                      projectId={project.id}
                      projectSlug={project.slug}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            // No preview available state
            <div className="flex items-center justify-center h-full p-8 bg-white">
              <div className="text-center space-y-4 max-w-md">
                <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <Monitor className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm mb-1 text-gray-900">{t('preview.states.noPreview.title')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('preview.states.noPreview.description')}
                  </p>
                </div>
              </div>
            </div>
          )}
            </div>
          )}
        </div>
      )}

      {/* Product Spec Content */}
      {!isProvisioning && !isManagedAgentProject && shouldRenderTab('spec') && (
        <div className={`flex-1 relative ${activeTab === 'spec' ? '' : 'hidden'}`}>
          <div className="absolute inset-0 overflow-auto">
            {effectiveProductSpec && effectiveProductSpec.content ? (
              <div className="p-6 prose prose-sm dark:prose-invert max-w-none bg-content-bg text-foreground">
                <div className="flex items-center justify-between mb-4">
                  <Badge variant="outline" className="text-xs border-border text-muted-foreground bg-muted">
                    {t('productSpec.lastUpdated')} {new Date(effectiveProductSpec.updated_at).toLocaleString()}
                  </Badge>
                </div>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkSectionize]}
                  components={markdownComponents}
                >
                  {effectiveProductSpec.content}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full p-8 bg-content-bg">
                <div className="text-center space-y-4">
                  <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <FileText className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1 text-foreground">{t('productSpec.empty.title')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('productSpec.empty.description')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Content */}
      {!isProvisioning && shouldRenderTab('history') && (
        <div className={`flex-1 relative ${activeTab === 'history' ? '' : 'hidden'}`}>
          <div className="absolute inset-0 overflow-auto">
            <HistoryTab
              historyEntries={historyEntries}
              historyPagy={historyPagy}
              projectId={project.id}
              deployedHistoryEntryId={project.deployed_history_entry_id}
              processing={processing}
              isRewinding={isRewinding}
              onRewindingChange={onRewindingChange}
            />
          </div>
        </div>
      )}

      {/* Integrations Content */}
      {!isProvisioning && !isManagedAgentProject && shouldRenderTab('integrations') && (
        <div className={`flex-1 relative ${activeTab === 'integrations' ? '' : 'hidden'}`}>
          <div className="absolute inset-0 overflow-auto">
            <IntegrationsTab project={project} />
          </div>
        </div>
      )}

      {/* Live Content */}
      {!isProvisioning && supportsLiveEnvironment && shouldRenderTab('live') && (
        <div className={`flex-1 relative ${activeTab === 'live' ? '' : 'hidden'}`}>
          <div className="absolute inset-0 overflow-auto">
            {project.django_stack ? (
              <DjangoLiveSection
                project={project}
                processing={processing}
                conversationId={conversationId}
                onDeploy={handleDeployClick}
                onRedeploy={handleRedeployClick}
                onRestart={handleRestartLiveClick}
                onViewDeployedInHistory={onViewDeployedInHistory}
                activeWorkflow={activeWorkflow}
                onAbortWorkflow={onAbortWorkflow}
                onRetryWorkflow={onRetryWorkflow}
                isAbortingWorkflow={isAbortingWorkflow}
                isRetryingWorkflow={isRetryingWorkflow}
              />
            ) : (
              <div className="bg-card">
                <div className="p-8 space-y-6 max-w-3xl mx-auto">
                  {/* Header */}
                  <div className="flex items-center gap-3">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      project.production_url
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : 'bg-red-100 dark:bg-red-900/30'
                    }`}>
                      <Globe className={`h-6 w-6 ${
                        project.production_url
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                        {project.production_url ? t('live.header.active') : t('live.header.inactive')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {project.production_url
                          ? t('live.header.activeDescription')
                          : t('live.header.inactiveDescription')}
                      </p>
                    </div>
                  </div>

                  {/* Warning message */}
                  {!project.all_integrations_have_live_vars && project.production_status !== 'deploying' && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">
                        {t('live.warnings.credentialsTitle')}
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {t('live.warnings.credentialsDescription')}
                      </p>
                    </div>
                  )}

                  {/* Deployment status messages */}
                  {project.production_status === 'deploying' && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                        <div className="text-left">
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                            {t('live.deployment.inProgress')}
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                            {project.production_url
                              ? t('live.deployment.inProgressUpdate')
                              : t('live.deployment.inProgressNew')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {project.production_status === 'deploy_failed' && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <div className="text-left">
                        <p className="text-sm font-medium text-red-900 dark:text-red-300 mb-1">
                          {t('live.deployment.failed')}
                        </p>
                        <p className="text-xs text-red-700 dark:text-red-400">
                          {t('live.deployment.failedDescription')}
                        </p>
                      </div>
                    </div>
                  )}

                  {project.production_status === 'deployed' && project.has_production_error && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                      <div className="text-left">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">
                          {t('live.deployment.redeployFailed')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Suspended due to insufficient credits */}
                  {isSuspendedNoCredits && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                      <div className="text-left">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">
                          {t('live.suspended.title')}
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          {t('live.suspended.description', { cost: project.live_hosting_cost })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Production URL and Preview - only shown when deployed */}
                  {project.production_url && (
                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{t('live.production.url')}</p>
                        <a
                          href={project.production_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:underline font-mono break-all flex items-center gap-2"
                        >
                          {project.production_url}
                          <ExternalLink className="h-4 w-4 flex-shrink-0" />
                        </a>
                        {project.production_deployed_at && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            {t('live.production.lastDeployed')} {new Date(project.production_deployed_at).toLocaleString(getDateLocale())}
                          </p>
                        )}
                        {/* View deployed version link */}
                        {project.deployed_history_entry_id && onViewDeployedInHistory && (
                          <button
                            onClick={onViewDeployedInHistory}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mt-2 flex items-center gap-1 transition-colors"
                          >
                            {t('live.production.viewDeployed')}
                            <ArrowRight className="h-3 w-3" />
                          </button>
                        )}
                      </div>

                      {/* Preview iframe */}
                      <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-900 relative h-64">
                        <iframe
                          src={project.production_url}
                          className="w-full h-full"
                          title={t('preview.productionIframeTitle')}
                          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
                          allow="microphone; camera; geolocation; clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; payment; web-share"
                        />
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute top-1 right-1 h-5 w-5 rounded shadow-sm pointer-events-auto z-10"
                          onClick={() => project.production_url && window.open(project.production_url, '_blank')}
                          title={t('live.production.openInNewTab')}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                        {/* Overlay to make iframe uninteractable */}
                        <div className="absolute inset-0 cursor-default" />
                      </div>

                      {/* Production Login Credentials */}
                      {project.production_user_email && project.production_user_password ? (
                        <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
                          <button
                            onClick={() => setShowProductionCredentials(!showProductionCredentials)}
                            className="flex items-center justify-between w-full mb-3 hover:opacity-70 transition-opacity"
                          >
                            <div className="flex items-center gap-2">
                              <KeyRound className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('live.credentials.title')}</p>
                            </div>
                            {showProductionCredentials ? (
                              <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            )}
                          </button>
                          {showProductionCredentials && (
                            <div className="space-y-3">
                              {/* Email */}
                              <div>
                                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t('live.credentials.email')}</label>
                                <div className="flex items-center gap-2">
                                  <code className="flex-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 rounded border border-gray-200 dark:border-gray-600">
                                    {project.production_user_email}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCopyProductionEmail}
                                    className="h-8 px-2"
                                  >
                                    {copiedProductionEmail ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                                  </Button>
                                </div>
                              </div>

                              {/* Password */}
                              <div>
                                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t('live.credentials.password')}</label>
                                <div className="flex items-center gap-2">
                                  <code className="flex-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 font-mono">
                                    {showProductionPassword ? project.production_user_password : '••••••••••••••••'}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowProductionPassword(!showProductionPassword)}
                                    className="h-8 px-2"
                                    title={showProductionPassword ? t('live.credentials.hidePassword') : t('live.credentials.showPassword')}
                                  >
                                    {showProductionPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCopyProductionPassword}
                                    className="h-8 px-2"
                                  >
                                    {copiedProductionPassword ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : project.production_status === 'deployed' && (
                        <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <KeyRound className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('live.credentials.title')}</p>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">{t('live.credentials.notGenerated')}</p>
                          <Button
                            onClick={handleGenerateCredentials}
                            disabled={isGeneratingCredentials}
                            size="sm"
                            variant="outline"
                          >
                            {isGeneratingCredentials ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                {t('live.credentials.generating')}
                              </>
                            ) : (
                              <>
                                <KeyRound className="h-4 w-4 mr-2" />
                                {t('live.credentials.generate')}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Live Integrations Section - Only show if there are sandbox integrations */}
                  {project.integrations.filter(i => i.sandbox_variables.length > 0).length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                      <LiveIntegrationsSection
                        key={`live-integrations-${project.integrations.map(i => `${i.id}-${i.has_live_variables}`).join('-')}`}
                        project={project}
                      />
                    </div>
                  )}

                  {/* Vanity Subdomain + DNS Management */}
                  {project.production_status === 'deployed' && (
                    <>
                      <VanitySubdomainSection project={project} />
                      <DnsManagementSection project={project} />
                    </>
                  )}

                  {/* Deploy Button */}
                  <div className="flex justify-end mt-6">
                    {(() => {
                      // Only disable "Deploy" if already live - "Restart Live" should always work
                      const isAlreadyLive = !isSuspendedNoCredits &&
                        project.deployed_history_entry_id != null &&
                        project.deployed_history_entry_id === project.current_history_entry_id
                      const isDisabled = processing ||
                        !project.all_integrations_have_live_vars ||
                        project.production_status === 'deploying' ||
                        isAlreadyLive

                      return (
                        <Button
                          onClick={isSuspendedNoCredits ? handleRestartLiveClick : (project.production_url ? handleRedeployClick : handleDeployClick)}
                          disabled={isDisabled}
                          size="lg"
                          title={isAlreadyLive ? t('live.deployment.alreadyLive') : undefined}
                        >
                          <Globe className="h-5 w-5 mr-2" />
                          {isSuspendedNoCredits ? t('live.buttons.restart') : t('live.buttons.deploy')}
                        </Button>
                      )
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assets Content */}
      {!isProvisioning && shouldRenderTab('assets') && (
        <div className={`flex-1 relative ${activeTab === 'assets' ? '' : 'hidden'}`}>
          <div className="absolute inset-0 overflow-hidden">
            {organization ? (
              <AssetsView
                ownerType="project"
                ownerId={project.id}
                projects={[]}
                isActive={activeTab === 'assets'}
              />
            ) : (
              <div className="flex items-center justify-center h-full p-8">
                <p className="text-muted-foreground">{t('preview.assets.notAvailable')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Content */}
      {!isProvisioning && shouldRenderTab('settings') && (
        <div className={`flex-1 relative ${activeTab === 'settings' ? '' : 'hidden'}`}>
          <div className="absolute inset-0 overflow-auto">
            <SettingsTab project={project} />
          </div>
        </div>
      )}


      {/* Deployment Confirmation Dialog */}
      <AlertDialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isSuspendedNoCredits
                ? t('preview.deploy.restartTitle')
                : isRedeployment
                  ? t('preview.deploy.redeployTitle')
                  : t('preview.deploy.deployTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {isSuspendedNoCredits ? (
                  <Trans
                    i18nKey="preview.deploy.restartDescription"
                    ns="conversations"
                    values={{ credits: project.live_hosting_cost }}
                    components={{ strong: <strong /> }}
                  />
                ) : isRedeployment ? (
                  <span>{t('preview.deploy.redeployDescription')}</span>
                ) : (
                  <Trans
                    i18nKey="preview.deploy.deployDescription"
                    ns="conversations"
                    values={{ credits: project.live_hosting_cost }}
                    components={{ strong: <strong /> }}
                  />
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('preview.deploy.cancelButton')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeploy}>
              {isSuspendedNoCredits ? t('preview.deploy.restartButton') : t('preview.deploy.deployButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile Preview QR Code Dialog */}
      <AlertDialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Test on Your Device
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Scan this QR code with the Expo Go app on your phone to preview the mobile app.
                </p>
                <div className="flex flex-col items-center gap-3 p-4 bg-white rounded-lg min-h-[250px] justify-center">
                  {tunnelUrlLoading && (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Loading tunnel URL...</p>
                    </div>
                  )}
                  {tunnelUrlError && !tunnelUrlLoading && (
                    <div className="flex flex-col items-center gap-2 text-center">
                      <AlertCircle className="h-8 w-8 text-destructive" />
                      <p className="text-sm text-destructive">{tunnelUrlError}</p>
                      <p className="text-xs text-muted-foreground">
                        Make sure the mobile app is running with tunnel mode enabled.
                      </p>
                    </div>
                  )}
                  {tunnelUrl && !tunnelUrlLoading && (
                    <>
                      <QRCodeSVG
                        value={tunnelUrl}
                        size={200}
                        level="M"
                        includeMargin={true}
                      />
                      <code className="text-xs text-muted-foreground break-all text-center max-w-full">
                        {tunnelUrl}
                      </code>
                    </>
                  )}
                </div>
                <div className="text-xs text-muted-foreground space-y-2">
                  <p><strong>Instructions:</strong></p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Install Expo Go from the links below</li>
                    <li>Open the app and tap "Scan QR code"</li>
                    <li>Point your camera at the QR code above</li>
                  </ol>
                  <div className="flex gap-2 pt-1">
                    <a
                      href="https://apps.apple.com/app/expo-go/id982107779"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-black text-white rounded text-xs hover:bg-gray-800 transition-colors"
                    >
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                      App Store
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=host.exp.exponent"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                    >
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
                      Play Store
                    </a>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:close')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  )
}

// Helper component to render tasks without the TodoPanel wrapper
function TasksContent({ groupedTodos }: { groupedTodos: GroupedTodos }) {
  const { t } = useTranslation('conversations')
  return (
    <>
      {/* In Progress */}
      {groupedTodos.in_progress.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Loader2 className="h-3 w-3 text-primary animate-spin" />
            <span className="text-[10px] font-medium text-foreground">
              {t('preview.tasks.inProgress', { count: groupedTodos.in_progress.length })}
            </span>
          </div>
          <div className="space-y-1.5">
            {groupedTodos.in_progress.map((todo, index) => (
              <TaskItem key={`in-progress-${index}`} todo={todo} status="in_progress" />
            ))}
          </div>
        </div>
      )}

      {/* Pending */}
      {groupedTodos.pending.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="text-[10px] font-medium text-muted-foreground">
              {t('preview.tasks.pending', { count: groupedTodos.pending.length })}
            </span>
          </div>
          <div className="space-y-1.5">
            {groupedTodos.pending.map((todo, index) => (
              <TaskItem key={`pending-${index}`} todo={todo} status="pending" />
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      {groupedTodos.completed.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <CheckCircle2 className="h-3 w-3 text-green-500" />
            <span className="text-[10px] font-medium text-green-600 dark:text-green-400">
              {t('preview.tasks.completed', { count: groupedTodos.completed.length })}
            </span>
          </div>
          <div className="space-y-1.5">
            {groupedTodos.completed.map((todo, index) => (
              <TaskItem key={`completed-${index}`} todo={todo} status="completed" />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function TaskItem({ todo, status }: { todo: any, status: 'pending' | 'in_progress' | 'completed' }) {
  return (
    <div className="flex items-start gap-1.5 text-[11px]">
      {status === 'completed' && (
        <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
      )}
      {status === 'in_progress' && (
        <Loader2 className="h-3 w-3 text-primary animate-spin flex-shrink-0 mt-0.5" />
      )}
      {status === 'pending' && (
        <Circle className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
      )}
      <span
        className={`flex-1 ${
          status === 'completed'
            ? 'text-muted-foreground line-through'
            : status === 'in_progress'
            ? 'text-foreground font-medium'
            : 'text-muted-foreground'
        }`}
      >
        {todo.content}
      </span>
    </div>
  )
}
